import { defineStore } from 'pinia'
import {
  getClientInfoFn,
  clientLoginFn,
  clientLogoutFn,
  adminLoginFn,
  adminLogoutFn,
  getAdminInfoFn
} from '@/services/api/restApiCalls'
import { email } from '@vuelidate/validators'

/**
 * Authentication Global Store
 * 
 * Usage Examples:
 * 
 * // In a component
 * import { useAuthGlobalStore } from '@/stores/authGlobalStore'
 * const authStore = useAuthGlobalStore()
 * 
 * // Check if user has a specific role
 * // Note: Super admins automatically return true for all role checks
 * if (authStore.hasRole('developer')) { ... }
 * 
 * // Check if user has any of multiple roles
 * // Note: Super admins automatically return true for all role checks
 * if (authStore.hasAnyRole(['developer', 'support_specialist'])) { ... }
 * 
 * // Check if user has all specified roles
 * // Note: Super admins automatically return true for all role checks
 * if (authStore.hasAllRoles(['developer', 'admin'])) { ... }
 * 
 * // Check if user has a specific permission group
 * // Note: Super admins automatically return true for all permission checks
 * if (authStore.hasPermission('menu_crud')) { ... }
 * 
 * // Check if user has any of multiple permission groups
 * // Note: Super admins automatically return true for all permission checks
 * if (authStore.hasAnyPermission(['menu_crud', 'printer_crud'])) { ... }
 * 
 * // Check if user has all specified permission groups
 * // Note: Super admins automatically return true for all permission checks
 * if (authStore.hasAllPermissions(['menu_crud', 'printer_crud'])) { ... }
 * 
 * // Get all role names
 * const roles = authStore.roleNames // ['developer', 'support_specialist']
 * 
 * // Get all permission group names
 * const permissions = authStore.permissionGroupNames // ['menu_crud', 'printer_crud']
 * 
 * // Get full role object
 * const devRole = authStore.getRole('developer') // { name: 'developer', role_id: 3, info: null }
 * 
 * // Get full permission group object
 * const menuPerm = authStore.getPermissionGroup('menu_crud') // { name: 'menu_crud', permission_group_id: 2, info: null }
 * 
 * // Check if user is super admin
 * if (authStore.isSuperAdmin) { ... }
 */
export const useAuthGlobalStore = defineStore('auth', {
  state: () => ({
    user: {},
    isStaffLoggedIn: false,
    isClientLoggedIn: false,
    returnUrl: null
  }),
  persist: true,
  getters: {
    // Get current user's roles
    userRoles: (state) => {
      if (state.isStaffLoggedIn && state.user.admin_auth_perm_data) {
        return state.user.admin_auth_perm_data.roles || []
      } else if (state.isClientLoggedIn && state.user.client_auth_perm_data) {
        return state.user.client_auth_perm_data.roles || []
      }
      return []
    },
    
    // Get current user's permission groups
    userPermissionGroups: (state) => {
      if (state.isStaffLoggedIn && state.user.admin_auth_perm_data) {
        return state.user.admin_auth_perm_data.permission_groups || []
      } else if (state.isClientLoggedIn && state.user.client_auth_perm_data) {
        return state.user.client_auth_perm_data.permission_groups || []
      }
      return []
    },
    
    // Check if user is staff
    isStaff: (state) => {
      if (state.isStaffLoggedIn && state.user.admin_auth_perm_data) {
        return Boolean(state.user.admin_auth_perm_data.is_staff)
      }
      return false
    },
    
    // Check if user is super admin
    isSuperAdmin: (state) => {
      if (state.isStaffLoggedIn && state.user.admin_auth_perm_data) {
        return state.user.admin_auth_perm_data.is_super_admin === 1
      }
      return false
    },
    
    // Get all role names as array
    roleNames: (state) => {
      const roles = state.isStaffLoggedIn && state.user.admin_auth_perm_data
        ? state.user.admin_auth_perm_data.roles || []
        : state.isClientLoggedIn && state.user.client_auth_perm_data
          ? state.user.client_auth_perm_data.roles || []
          : []
      return roles.map(role => role.name)
    },
    
    // Get all permission group names as array
    permissionGroupNames: (state) => {
      const groups = state.isStaffLoggedIn && state.user.admin_auth_perm_data
        ? state.user.admin_auth_perm_data.permission_groups || []
        : state.isClientLoggedIn && state.user.client_auth_perm_data
          ? state.user.client_auth_perm_data.permission_groups || []
          : []
      return groups.map(group => group.name)
    }
  },
  actions: {
    // Check if user has a specific role
    hasRole(roleName) {
      // Super admins have all roles
      if (this.isSuperAdmin) return true
      return this.roleNames.includes(roleName)
    },
    
    // Check if user has any of the specified roles
    hasAnyRole(roleNames) {
      // Super admins have all roles
      if (this.isSuperAdmin) return true
      if (!Array.isArray(roleNames)) {
        roleNames = [roleNames]
      }
      return roleNames.some(roleName => this.roleNames.includes(roleName))
    },
    
    // Check if user has all of the specified roles
    hasAllRoles(roleNames) {
      // Super admins have all roles
      if (this.isSuperAdmin) return true
      if (!Array.isArray(roleNames)) {
        roleNames = [roleNames]
      }
      return roleNames.every(roleName => this.roleNames.includes(roleName))
    },
    
    // Check if user has a specific permission group
    hasPermission(permissionGroupName) {
      // Super admins have all permissions
      if (this.isSuperAdmin) return true
      return this.permissionGroupNames.includes(permissionGroupName)
    },
    
    // Check if user has any of the specified permission groups
    hasAnyPermission(permissionGroupNames) {
      // Super admins have all permissions
      if (this.isSuperAdmin) return true
      if (!Array.isArray(permissionGroupNames)) {
        permissionGroupNames = [permissionGroupNames]
      }
      return permissionGroupNames.some(name => this.permissionGroupNames.includes(name))
    },
    
    // Check if user has all of the specified permission groups
    hasAllPermissions(permissionGroupNames) {
      // Super admins have all permissions
      if (this.isSuperAdmin) return true
      if (!Array.isArray(permissionGroupNames)) {
        permissionGroupNames = [permissionGroupNames]
      }
      return permissionGroupNames.every(name => this.permissionGroupNames.includes(name))
    },
    
    // Get role by name
    getRole(roleName) {
      return this.userRoles.find(role => role.name === roleName)
    },
    
    // Get permission group by name
    getPermissionGroup(permissionGroupName) {
      return this.userPermissionGroups.find(group => group.name === permissionGroupName)
    },
    async adminLogin(email, password) {
      let loginFormData = new FormData()
      loginFormData.append('email', email)
      loginFormData.append('password', password)

      await adminLoginFn(loginFormData)
        .then((user_data) => {
          console.log('User login', user_data)
          this.setAdminAuthUser(user_data || {})
        })
        .catch(() => {
          this.user = {}
          this.isStaffLoggedIn = false
          this.isClientLoggedIn = false
        })
      return this.user
    },
    async adminLogout() {
      try {
        let user = await adminLogoutFn()
        this.setAdminAuthUser(user || {})
      } catch (error) {
        console.error('Logout failed:', error)
        this.setAdminAuthUser({})
      }
    },

    async getAdminAuthUser() {
      // make api call to get auth user here
      let user = await getAdminInfoFn()
      return user
    },

    setAdminAuthUser(user) {
      // this is setter method to set auth user
      if (user && user.email && user.first_name && user.auth_perm_data && user.auth_perm_data.is_staff) {
        this.user = {
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          admin_auth_perm_data: user.auth_perm_data
        }
        this.isStaffLoggedIn = true
        this.isClientLoggedIn = false
      } else {
        this.user = {}
        this.isStaffLoggedIn = false
        this.isClientLoggedIn = false
      }
    },

    // START: client login
    async clientLogin(email, password) {
      let loginFormData = new FormData()
      loginFormData.append('email', email)
      loginFormData.append('password', password)

      await clientLoginFn(loginFormData)
        .then((user_data) => {
          console.log('User login', user_data)
          this.setClientAuthUser(user_data || {})
        })
        .catch(() => {
          this.user = {}
          this.isStaffLoggedIn = false
          this.isClientLoggedIn = false
        })
      return this.user
    },
    async clientLogout() {
      try {
        let user = await clientLogoutFn()
        this.setClientAuthUser(user || {})
      } catch (error) {
        console.error('Client logout failed:', error)
        this.setClientAuthUser({})
      }
    },

    async getClientAuthUser() {
      // make api call to get auth user here
      let user = await getClientInfoFn()
      return user
    },

    setClientAuthUser(user) {
      // this is setter method to set auth user
      if (user && user.email && user.first_name) {
        this.user = {
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          client_auth_perm_data: user.auth_perm_data,
          resto_id: user.client_resto_id,
          resto: user.client
        }
        this.isClientLoggedIn = true
        this.isStaffLoggedIn = false
      } else {
        this.user = {}
        this.isStaffLoggedIn = false
        this.isClientLoggedIn = false
      }
    }
  }
})
